import logger from '@/utils/logger';
import React, { useState } from 'react';
import { Mail, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import PublicLayout from '@/components/layout/PublicLayout';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://clairify.fr/#localbusiness',
  name: 'Clairify (STELLARMEET SAS)',
  description: 'Solution IA Business Intelligence pour PME. Analyse de données avec mémoire persistante.',
  url: 'https://clairify.fr',
  telephone: '',
  email: 'contact@clairify.fr',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '25 rue de Ponthieu',
    addressLocality: 'Paris',
    postalCode: '75008',
    addressRegion: 'Île-de-France',
    addressCountry: 'FR',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 48.8738,
    longitude: 2.3086,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  ],
  priceRange: '€€',
  image: 'https://clairify.fr/og-image.png',
  sameAs: [
    'https://linkedin.com/company/clairify',
    'https://twitter.com/clairify_ai',
  ],
};

export default function Contact() {
 const [formData, setFormData] = useState({
 name: '',
 email: '',
 company: '',
 subject: '',
 message: ''
 });
 const [isSubmitting, setIsSubmitting] = useState(false);
 const [isSubmitted, setIsSubmitted] = useState(false);

 const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
 setFormData({
 ...formData,
 [e.target.name]: e.target.value
 });
 };

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setIsSubmitting(true);
 
 try {
 // Utiliser l'URL backend appropriée selon l'environnement
 const backendUrl = window.location.hostname === 'localhost'
 ? 'http://localhost:3000'
 : 'https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud';
 
 const response = await fetch(`${backendUrl}/api/contact`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 name: formData.name.trim(),
 email: formData.email.toLowerCase().trim(),
 company: formData.company.trim() || null,
 subject: formData.subject,
 message: formData.message.trim(),
 }),
 });

 const data = await response.json();

 if (!response.ok) {
 throw new Error(data.error || 'Erreur lors de l\'envoi du message');
 }

 setIsSubmitted(true);
 
 // Réinitialiser le formulaire après 3 secondes
 setTimeout(() => {
 setIsSubmitted(false);
 setFormData({
 name: '',
 email: '',
 company: '',
 subject: '',
 message: ''
 });
 }, 5000);
 } catch (error: any) {
 logger.error(' Erreur envoi message:', error);
 alert(error.message || 'Une erreur est survenue. Veuillez réessayer.');
 } finally {
 setIsSubmitting(false);
 }
 };

 return (
 <PublicLayout>
 <SEOHead
 title="Contact Clairify — IA Business Intelligence PME | Paris 8e"
 description="Contactez l'équipe Clairify : démonstration, tarifs, support technique. STELLARMEET SAS, 25 rue de Ponthieu, 75008 Paris. Email : contact@clairify.fr. Réponse sous 24-48h."
 keywords={['contact Clairify', 'démonstration IA PME', 'support Clairify', 'STELLARMEET contact', 'IA business intelligence contact', 'devis IA analyse données']}
 structuredData={[localBusinessSchema]}
 />

 <section className="pub-hero">
 <p className="home-kicker">Contact</p>
 <h1>Écrivez-nous.</h1>
 <p>Une question, une démo, un projet. Réponse sous 24 à 48 heures ouvrées.</p>
 </section>

 <section className="pub-wrap">
 <div className="grid lg:grid-cols-2 gap-12 items-start">
 <div className="form-panel">
 <h2>Message</h2>

 {isSubmitted ? (
 <div className="bg-green-500/10 border border-green-500/20 p-6 rounded-xl text-center">
 <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
 <p className="text-lg font-semibold text-theme-primary mb-2">Message envoyé avec succès !</p>
 <p className="text-sm text-theme-secondary">Nous vous répondrons dans les plus brefs délais.</p>
 </div>
 ) : (
 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="grid sm:grid-cols-2 gap-4">
 <div>
 <label htmlFor="name" className="block text-sm font-medium text-theme-primary mb-2">
 Nom complet *
 </label>
 <input
 type="text"
 id="name"
 name="name"
 required
 value={formData.name}
 onChange={handleChange}
 className="w-full px-4 py-3 bg-theme-base border border-theme text-theme-primary"
 placeholder="Jean Dupont"
 />
 </div>
 <div>
 <label htmlFor="email" className="block text-sm font-medium text-theme-primary mb-2">
 Email professionnel *
 </label>
 <input
 type="email"
 id="email"
 name="email"
 required
 value={formData.email}
 onChange={handleChange}
 className="w-full px-4 py-3 bg-theme-base border border-theme text-theme-primary"
 placeholder="jean.dupont@entreprise.fr"
 />
 </div>
 </div>

 <div>
 <label htmlFor="company" className="block text-sm font-medium text-theme-primary mb-2">
 Entreprise
 </label>
 <input
 type="text"
 id="company"
 name="company"
 value={formData.company}
 onChange={handleChange}
 className="w-full px-4 py-3 bg-theme-base border border-theme text-theme-primary"
 placeholder="Nom de votre entreprise"
 />
 </div>

 <div>
 <label htmlFor="subject" className="block text-sm font-medium text-theme-primary mb-2">
 Sujet *
 </label>
 <select
 id="subject"
 name="subject"
 required
 value={formData.subject}
 onChange={handleChange}
 className="w-full px-4 py-3 bg-theme-base border border-theme text-theme-primary"
 >
 <option value="">Sélectionnez un sujet</option>
 <option value="demo">Demande de démonstration</option>
 <option value="pricing">Question sur les tarifs</option>
 <option value="partnership">Partenariat</option>
 <option value="support">Support technique</option>
 <option value="other">Autre</option>
 </select>
 </div>

 <div>
 <label htmlFor="message" className="block text-sm font-medium text-theme-primary mb-2">
 Message *
 </label>
 <textarea
 id="message"
 name="message"
 required
 rows={6}
 value={formData.message}
 onChange={handleChange}
 className="w-full px-4 py-3 bg-theme-base border border-theme text-theme-primary resize-none"
 placeholder="Décrivez votre demande..."
 />
 </div>

 <Button
 type="submit"
 disabled={isSubmitting}
 className="w-full text-base py-6"
 >
 {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
 </Button>
 </form>
 )}
 </div>

 {/* Informations de contact */}
 <div className="space-y-6">
 {/* Informations entreprise */}
 <div className="form-panel">
 <h2>STELLARMEET</h2>
 <div className="space-y-4 text-theme-secondary">
 <div>
 <p className="font-semibold text-theme-primary mb-1">STELLARMEET</p>
 <p className="text-sm">SAS au capital de 1 000,00 €</p>
 <p className="text-sm">SIREN : 935 065 094</p>
 <p className="text-sm">SIRET : 935 065 094 00010</p>
 <p className="text-sm">N° TVA : FR11 935 065 094</p>
 </div>
 <div className="flex items-start gap-3 pt-4 border-t border-theme-border">
 <MapPin className="w-5 h-5 text-[var(--spark-gold)] flex-shrink-0 mt-1" />
 <div>
 <p className="font-semibold text-theme-primary mb-1">Siège social</p>
 <p className="text-sm">25 rue de Ponthieu</p>
 <p className="text-sm">75008 Paris, France</p>
 </div>
 </div>
 </div>
 </div>

 {/* Coordonnées */}
 <div className="form-panel">
 <h2>Coordonnées</h2>
 <div className="space-y-4">
 <div className="flex items-start gap-3">
 <Mail className="w-5 h-5 text-[var(--spark-gold)] flex-shrink-0 mt-1" />
 <div>
 <p className="font-semibold text-theme-primary mb-1">Email professionnel</p>
 <a 
 href="mailto:contact@clairify.fr" 
 className="text-[var(--spark-gold)] hover:underline text-sm"
 >
 contact@clairify.fr
 </a>
 </div>
 </div>
 <div className="pt-4 border-t border-theme-border">
 <p className="text-sm text-theme-secondary mb-3">
 Pour toute question relative à la protection de vos données personnelles :
 </p>
 <a 
 href="mailto:privacy@clairify.fr" 
 className="text-[var(--spark-gold)] hover:underline text-sm"
 >
 privacy@clairify.fr
 </a>
 </div>
 </div>
 </div>

 {/* Horaires */}
 <div className="form-panel">
 <h3 className="font-display text-xl font-medium text-theme-primary mb-4">Horaires</h3>
 <div className="space-y-2 text-theme-secondary">
 <p className="text-sm"><strong className="text-theme-primary">Lundi - Vendredi :</strong> 9h00 - 18h00 (CET)</p>
 <p className="text-sm"><strong className="text-theme-primary">Samedi - Dimanche :</strong> Fermé</p>
 <p className="text-xs text-theme-tertiary mt-4">
 Nous nous efforçons de répondre à toutes les demandes dans un délai de 24 à 48 heures ouvrées.
 </p>
 </div>
 </div>
 </div>
 </div>
 </section>
 </PublicLayout>
 );
}

